import { env } from "@/lib/env";

export interface ApiErrorBody {
  detail?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
    message?: string,
  ) {
    super(message ?? body.detail ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "POST";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function apiRequest<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const response = await fetch(`${env.apiUrl}${path}`, {
    method: opts.method ?? "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers ?? {}),
    },
    signal: opts.signal,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as ApiErrorBody | T)
    : ((await response.text()) as unknown as T);

  if (!response.ok) {
    throw new ApiError(response.status, payload as ApiErrorBody);
  }

  return payload as T;
}

export const api = {
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    apiRequest<T>(path, { ...opts, method: "POST", body }),
};
